import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Clock, CheckCircle, AlertCircle, X, RefreshCw } from 'lucide-react';


const ActiveJobCard = ({ job, onDownload, onCancel }) => {
  const { t } = useTranslation();

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusInfo = {
    pending: {
      icon: <Clock size={16} className="text-yellow-600" />,
      text: t('report.pending'),
      textColor: 'text-yellow-800',
      bgColor: 'bg-yellow-100',
      borderColor: 'border-yellow-400',
    },
    processing: {
      icon: <RefreshCw size={16} className="text-blue-600 animate-spin" />,
      text: t('report.processing'),
      textColor: 'text-blue-800',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-400',
    },
    completed: {
      icon: <CheckCircle size={16} className="text-green-600" />,
      text: t('report.completed'),
      textColor: 'text-green-800',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-400',
    },
    failed: {
      icon: <AlertCircle size={16} className="text-red-600" />,
      text: t('report.failed'),
      textColor: 'text-red-800',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-400',
    },
    default: {
      icon: <Clock size={16} className="text-gray-500" />,
      text: t('report.unknown'),
      textColor: 'text-gray-800',
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-400',
    },
  };

  const currentStatus = statusInfo[job.status] || statusInfo.default;

  return (
    <div className={`bg-white rounded-xl border ${currentStatus.borderColor} shadow-sm transition-all hover:shadow-md`}>
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-gray-800 text-md">
              {job.report_type === 'candidates' ? t('report.candidatesReport') : t('report.interviewsReport')}
            </h3>
            <div className={`inline-flex items-center gap-2 mt-2 px-2.5 py-1 rounded-full text-xs font-medium ${currentStatus.bgColor} ${currentStatus.textColor}`}>
              {currentStatus.icon}
              <span>{currentStatus.text}</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {job.status === 'completed' && (
              <button
                onClick={() => onDownload(job.id)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition-all"
                title={t('report.downloadReport')}
              >
                <Download size={16} />
                <span>{t('report.download')}</span>
              </button>
            )}
            {(job.status === 'pending' || job.status === 'processing') && (
              <button
                onClick={() => onCancel(job.id)}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors"
                title={t('report.cancelJob')}
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 bg-gray-50/50 p-4 text-sm rounded-b-xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="truncate">
                <p className="text-gray-500 text-xs mb-1">{t('report.jobId')}</p>
                <p className="font-mono text-xs text-gray-700 truncate" title={job.id}>{job.id}</p>
            </div>
            <div className="truncate">
                <p className="text-gray-500 text-xs mb-1">{t('report.formatLabel')}</p>
                <p className="font-medium text-gray-800 capitalize">{job.format || 'PDF'}</p>
            </div>
            <div className="truncate">
                <p className="text-gray-500 text-xs mb-1">{t('report.templateLabel')}</p>
                <p className="font-medium text-gray-800 capitalize">{job.template_name || job.template || t('report.basic')}</p>
            </div>
            <div className="truncate">
                <p className="text-gray-500 text-xs mb-1">{t('report.created')}</p>
                <p className="font-medium text-gray-800">{formatDate(job.created_at)}</p>
            </div>
        </div>
      </div>

      {job.status === 'failed' && job.error_message && (
        <div className="border-t border-red-200 bg-red-50 p-4 rounded-b-xl">
          <div className="flex items-start">
            <AlertCircle size={18} className="text-red-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-red-800">{t('report.errorDetails')}</h4>
              <p className="text-red-700 text-sm mt-1">{job.error_message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveJobCard;
